import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button,
  IconButton, Avatar, CircularProgress, Dialog, DialogContent, Menu, MenuItem, DialogActions, DialogTitle
} from '@mui/material';
import { Send, AttachFile, Person, Close, InsertDriveFile, MoreVert, Delete, DeleteSweep } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import { requestNotificationPermission, notifyNewMessage, unlockAudio } from '../utils/notifications';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ChatPage() {
  const navigate = useNavigate();
  const { receiverId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  
  // Verwende useRef statt useState für persistente Message-IDs
  const lastMessageIdsRef = useRef(new Set());
  const lastMessageCountRef = useRef(0);

  // Reset refs when receiverId changes
  useEffect(() => {
    lastMessageIdsRef.current = new Set();
    lastMessageCountRef.current = 0;
    console.log('🔄 Refs zurückgesetzt für neuen Empfänger:', receiverId);
  }, [receiverId]);

  // Notification Permission beim Laden anfordern
  useEffect(() => {
    requestNotificationPermission();
    
    // Audio entsperren beim ersten Click/Touch
    const handleUserInteraction = () => {
      unlockAudio();
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    loadMessages();
    loadReceiverInfo();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [receiverId]);

  useEffect(() => {
    // Nur nach unten scrollen, wenn der User bereits unten ist
    const shouldScroll = isUserAtBottom();
    if (shouldScroll) {
      scrollToBottom();
    }
  }, [messages]);

  const isUserAtBottom = () => {
    if (!messagesContainerRef.current) return true; // Beim ersten Laden immer scrollen
    
    const container = messagesContainerRef.current;
    const threshold = 150; // 150px Toleranz vom Ende
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // User ist am Ende wenn: scrollTop + clientHeight >= scrollHeight - threshold
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    console.log('Scroll Check:', { scrollTop, scrollHeight, clientHeight, isAtBottom });
    return isAtBottom;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to construct file URL correctly (prevents double /api/ prefix)
  const getFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    // If file_url already starts with /api/, use it directly with base domain
    if (fileUrl.startsWith('/api/')) {
      return `${API_URL.replace('/api', '')}${fileUrl}`;
    }
    // Otherwise, add API_URL (which includes /api)
    return `${API_URL}${fileUrl}`;
  };

  const loadReceiverInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const receiver = response.data.users.find(u => u.username === receiverId);
      if (receiver) setReceiverName(receiver.full_name);
    } catch (error) {
      console.error('Error loading receiver info:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversationMessages = response.data.messages.filter(msg => 
        (msg.sender_id === user?.username && msg.receiver_id === receiverId) ||
        (msg.sender_id === receiverId && msg.receiver_id === user?.username) ||
        msg.receiver_id === 'all'
      );
      
      console.log('📨 Messages:', {
        total: conversationMessages.length,
        lastCount: lastMessageCountRef.current,
        lastIdsSize: lastMessageIdsRef.current.size
      });
      
      // Prüfe auf neue Nachrichten basierend auf Message-IDs
      if (lastMessageIdsRef.current.size > 0) {
        const newMessages = conversationMessages.filter(msg => !lastMessageIdsRef.current.has(msg._id));
        
        console.log('🔍 Debug Benachrichtigungen:', {
          lastIdsSize: lastMessageIdsRef.current.size,
          newMessagesFound: newMessages.length,
          newMessageIds: newMessages.map(m => m._id),
          senderIds: newMessages.map(m => m.sender_id),
          allNewMessages: newMessages.map(m => ({
            _id: m._id,
            sender: m.sender_id,
            receiver: m.receiver_id,
            content: m.content?.substring(0, 30),
            timestamp: m.timestamp
          }))
        });
        
        if (newMessages.length > 0) {
          console.log('🔔 Neue Nachrichten erkannt:', newMessages.length);
          
          newMessages.forEach(msg => {
            console.log('🔍 Prüfe Nachricht:', {
              _id: msg._id,
              sender_id: msg.sender_id,
              receiver_id: msg.receiver_id,
              receiverId: receiverId,
              currentUser: user?.username,
              isFromReceiver: msg.sender_id === receiverId,
              isToMe: msg.receiver_id === user?.username,
              willNotify: msg.sender_id === receiverId
            });
            
            // Nur Benachrichtigung für empfangene Nachrichten (nicht eigene)
            if (msg.sender_id === receiverId) {
              console.log('🔊 Spiele Benachrichtigung ab für:', msg);
              const preview = msg.message_type === 'text' 
                ? msg.content.substring(0, 50) + (msg.content.length > 50 ? '...' : '')
                : msg.message_type === 'image' ? '📷 Bild' : '📄 Datei';
              
              notifyNewMessage(receiverName || receiverId, preview);
              console.log('✅ Benachrichtigung abgespielt!');
            } else {
              console.log('⏭️ Überspringe - ist eigene Nachricht (sender=' + msg.sender_id + ', receiverId=' + receiverId + ')');
            }
          });
        } else {
          console.log('ℹ️ Keine neuen Nachrichten gefunden');
        }
      } else {
        console.log('🆕 Erste Ladung - noch keine Message-IDs gespeichert');
      }
      
      // Update tracked message IDs using refs
      const newIds = new Set(conversationMessages.map(msg => msg._id));
      console.log('💾 Aktualisiere Refs:', {
        vorher: { count: lastMessageCountRef.current, idsSize: lastMessageIdsRef.current.size },
        nachher: { count: conversationMessages.length, idsSize: newIds.size }
      });
      lastMessageIdsRef.current = newIds;
      lastMessageCountRef.current = conversationMessages.length;
      setMessages(conversationMessages);
      
      conversationMessages.forEach(async (msg) => {
        if (msg.receiver_id === user?.username && !msg.is_read) {
          try {
            await axios.post(
              `${API_URL}/messages/${msg._id}/mark-read`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Error marking message as read:', error);
          }
        }
      });
      
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Nur JPG, PNG, HEIC und PDF Dateien sind erlaubt');
      return;
    }

    const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxMB = file.type === 'application/pdf' ? 10 : 5;
      alert(`Datei zu groß. Maximum: ${maxMB}MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      let fileUrl = null;
      let fileName = null;
      let messageType = 'text';

      if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await axios.post(
          `${API_URL}/messages/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        fileUrl = uploadResponse.data.file_url;
        fileName = uploadResponse.data.file_name;
        messageType = selectedFile.type.startsWith('image/') ? 'image' : 'pdf';
        setUploading(false);
      }

      const messageData = {
        receiver_id: receiverId,
        message_type: messageType,
        content: newMessage.trim() || fileName
      };

      if (fileUrl) {
        messageData.file_url = fileUrl;
        messageData.file_name = fileName;
      }

      await axios.post(
        `${API_URL}/messages`,
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      setSelectedFile(null);
      setImagePreview(null);
      loadMessages();
      
      // Fokussiere Nachrichtenfeld wieder für nächste Nachricht
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMessages();
      setAnchorEl(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Fehler beim Löschen der Nachricht');
    }
  };

  const handleDeleteAllMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Verwende neuen Batch-Delete Endpoint
      await axios.delete(`${API_URL}/messages/conversation/${receiverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeleteDialogOpen(false);
      loadMessages();
    } catch (error) {
      console.error('Error deleting all messages:', error);
      alert('Fehler beim Löschen des Chatverlaufs');
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    // Backend sendet UTC Zeit im ISO-Format ohne 'Z' suffix
    // Wir müssen 'Z' hinzufügen um es als UTC zu parsen
    let date = new Date(dateString);
    
    // Wenn kein 'Z' am Ende und das Datum gültig aussieht, füge 'Z' hinzu
    if (!dateString.endsWith('Z') && !dateString.includes('+')) {
      date = new Date(dateString + 'Z');
    }
    
    // Wenn das Datum ungültig ist, Fallback
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    // Konvertiere zu deutscher Lokalzeit
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Heute';
    if (date.toDateString() === yesterday.toDateString()) return 'Gestern';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(msg => {
      const date = formatDate(msg.created_at);
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  const renderMessageContent = (msg) => {
    const isSent = msg.sender_id === user?.username;

    if (msg.message_type === 'image' && msg.file_url) {
      return (
        <Box>
          {msg.content && msg.content !== msg.file_name && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {msg.content}
            </Typography>
          )}
          <Box
            component="img"
            src={getFileUrl(msg.file_url)}
            alt={msg.file_name}
            sx={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: 1,
              cursor: 'pointer'
            }}
            onClick={() => setImagePreview(getFileUrl(msg.file_url))}
          />
        </Box>
      );
    }

    if (msg.message_type === 'pdf' && msg.file_url) {
      return (
        <Box>
          {msg.content && msg.content !== msg.file_name && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {msg.content}
            </Typography>
          )}
          <Paper
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: isSent ? 'rgba(255,255,255,0.2)' : '#f5f5f5',
              cursor: 'pointer'
            }}
            onClick={() => window.open(getFileUrl(msg.file_url), '_blank')}
          >
            <InsertDriveFile />
            <Typography variant="body2">{msg.file_name}</Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {msg.content}
      </Typography>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppHeader 
        title={receiverName || receiverId} 
        showBack={true} 
        backTo="/messages"
      />
      
      {/* Löschen-Button für gesamten Chat */}
      <Box sx={{ p: 1, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<DeleteSweep />}
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={messages.length === 0}
        >
          Chatverlauf löschen
        </Button>
      </Box>

      <Box 
        ref={messagesContainerRef}
        sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#f5f5f5', p: 2 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {Object.keys(messageGroups).length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4, color: '#757575' }}>
                <Typography>Noch keine Nachrichten</Typography>
                <Typography variant="body2">Sende die erste Nachricht!</Typography>
              </Box>
            ) : (
              Object.entries(messageGroups).map(([date, msgs]) => (
                <Box key={date}>
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Typography variant="caption" sx={{ 
                      bgcolor: '#e0e0e0', 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 2,
                      display: 'inline-block'
                    }}>
                      {date}
                    </Typography>
                  </Box>
                  {msgs.map((msg, index) => {
                    const isSent = msg.sender_id === user?.username;
                    const isBroadcast = msg.receiver_id === 'all';
                    const hasAttachment = msg.message_type === 'image' || msg.message_type === 'pdf';
                    
                    return (
                      <Box
                        key={msg._id || index}
                        sx={{
                          display: 'flex',
                          justifyContent: isSent ? 'flex-end' : 'flex-start',
                          mb: 1,
                          alignItems: 'flex-start'
                        }}
                      >
                        <Paper
                          sx={{
                            p: 1.5,
                            maxWidth: '70%',
                            bgcolor: isSent ? '#1976d2' : '#fff',
                            color: isSent ? '#fff' : '#000',
                            borderRadius: 2,
                            boxShadow: 1,
                            position: 'relative'
                          }}
                        >
                          {!isSent && (
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              {msg.sender_name}
                            </Typography>
                          )}
                          {isBroadcast && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              mb: 0.5, 
                              fontStyle: 'italic',
                              opacity: 0.8
                            }}>
                              📢 Broadcast an alle
                            </Typography>
                          )}
                          {renderMessageContent(msg)}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                            {formatTime(msg.created_at)}
                          </Typography>
                        </Paper>
                        {(isSent || hasAttachment) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setSelectedMsg(msg);
                            }}
                            sx={{ ml: isSent ? 0.5 : 0, mr: isSent ? 0 : 0.5, order: isSent ? 1 : -1 }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      <Paper sx={{ p: 2, borderTop: '1px solid #e0e0e0' }} elevation={3}>
        {selectedFile && (
          <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              {selectedFile.type.startsWith('image/') ? '🖼️' : '📄'} {selectedFile.name}
            </Typography>
            <IconButton size="small" onClick={() => setSelectedFile(null)}>
              <Close />
            </IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/jpeg,image/png,image/heic,application/pdf"
            onChange={handleFileSelect}
          />
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploading}
          >
            <AttachFile />
          </IconButton>
          <TextField
            inputRef={messageInputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Nachricht eingeben..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending || uploading}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !selectedFile) || sending || uploading}
            sx={{ minWidth: '56px', height: '56px' }}
          >
            {sending || uploading ? <CircularProgress size={24} color="inherit" /> : <Send />}
          </Button>
        </Box>
      </Paper>

      {/* Message Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedMsg?.message_type === 'image' && selectedMsg?.file_url && (
          <MenuItem onClick={() => {
            const link = document.createElement('a');
            link.href = getFileUrl(selectedMsg.file_url);
            link.download = selectedMsg.file_name || 'image.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setAnchorEl(null);
          }}>
            <InsertDriveFile fontSize="small" sx={{ mr: 1 }} />
            Herunterladen
          </MenuItem>
        )}
        {selectedMsg?.message_type === 'pdf' && selectedMsg?.file_url && (
          <MenuItem onClick={() => {
            window.open(getFileUrl(selectedMsg.file_url), '_blank');
            setAnchorEl(null);
          }}>
            <InsertDriveFile fontSize="small" sx={{ mr: 1 }} />
            PDF öffnen
          </MenuItem>
        )}
        {selectedMsg?.sender_id === user?.username && (
          <MenuItem onClick={() => {
            if (selectedMsg) handleDeleteMessage(selectedMsg._id);
          }}>
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Löschen
          </MenuItem>
        )}
      </Menu>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!imagePreview}
        onClose={() => setImagePreview(null)}
        maxWidth="lg"
      >
        <DialogContent>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              sx={{ position: 'absolute', top: 0, right: 0, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
              onClick={() => setImagePreview(null)}
            >
              <Close />
            </IconButton>
            <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bestätigungs-Dialog für Chatverlauf löschen */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Chatverlauf löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie wirklich den gesamten Chatverlauf mit {receiverName || receiverId} löschen? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteAllMessages} color="error" variant="contained">
            Alle Nachrichten löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
